import React, { useState } from 'react';
import { TextField, Box } from '@mui/material';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import InputField from 'packages/novous_web/src/common/components/InputField';
import DateField from 'packages/novous_web/src/common/components/DateField';
import dayjs from 'dayjs';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import Button from 'packages/novous_web/src/common/components/Button';

type Props = {
    renewData: any;
    onSave: (data: any) => void;
    activeStep: number;
    isSummaryView: boolean;
    handleBack: () => void;
    handleActiveState: (step: number) => void;
    setIsSummaryView: (val: boolean) => void;
};

const RenewOrderDetailsForm = ({
    renewData,
    onSave,
    activeStep,
    isSummaryView,
    handleBack,
    handleActiveState,
    setIsSummaryView
}: Props) => {
    const [formData, setFormData] = useState(renewData);
    const [submittedData, setSubmittedData] = useState<any[]>([renewData]);

    const calculateDurationInSeconds = (
        durationValue: string,
        durationUnit: string,
        startDate: string | null
    ): number => {
        const val = parseInt(durationValue, 10);
        if (isNaN(val) || !startDate) return 0;

        const start = dayjs(startDate);

        switch (durationUnit.toLowerCase()) {
            case 'years':
                return val * 365 * 24 * 60 * 60;
            case 'months': {
                let totalSeconds = 0;
                for (let i = 0; i < val; i++) {
                    let monthDate = start.add(i, 'month');
                    if (!monthDate.isValid()) {
                        const tempMonth = start.add(i, 'month');
                        monthDate = dayjs(`${tempMonth.year()}-${(tempMonth.month() + 1).toString().padStart(2, '0')}-01`).endOf('month');
                    }

                    const daysInMonth = monthDate.daysInMonth();
                    totalSeconds += daysInMonth * 24 * 60 * 60;
                }
                return totalSeconds;
            }
            case 'days':
                return val * 24 * 60 * 60;
            default:
                return 0;
        }
    };

    const getDisplayDuration = (seconds: number, unit: string): number => {
        switch (unit.toLowerCase()) {
            case 'years':
                return seconds / (365 * 24 * 60 * 60);
            case 'months':
                return seconds / (30 * 24 * 60 * 60);
            case 'days':
                return seconds / (24 * 60 * 60);
            default:
                return seconds;
        }
    };

    const handleDurationChange = (value: string, unit: string) => {
        const durationInSeconds = calculateDurationInSeconds(
            value,
            unit,
            formData.startDate
        );

        setFormData({
            ...formData,
            SelectDurationValue: value,
            SelectDurationUnit: unit,
            durations: durationInSeconds
        });
    };

    const handleChange = (field: string) => (e: { target: { value: any; }; }) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const handleSubmit = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleViewSummary = () => {
        if (!submittedData.length) {
            alert('Please add at least one item');
            return;
        }

        handleActiveState(2);
        setIsSummaryView(true);
        onSave(formData);
    };

    return (
        <div className="flex flex-col gap-4 h-full justify-between">
            <div className="flex flex-col gap-4">

                {activeStep === 1 && !isSummaryView && (
                    <>
                        <div className="relative grid grid-cols-2 rounded-lg gap-4 mt-8">
                            <InputField
                                label="Selected Item"
                                placeholder="Selected Item"
                                value={formData.productItem}
                                onChange={(e: any) => setFormData({ ...formData, selectItem: e })}
                                isDisabled
                            />
                            <InputField
                                label="Selected Device Type"
                                placeholder="Selected Device Type"
                                value={formData.deviceType}
                                onChange={(e: any) => setFormData({ ...formData, selectDeviceType: e })}
                                isDisabled
                            />
                            <InputField
                                label="Selected Quantity"
                                placeholder="Selected Quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange('quantity')}
                                showIncrementButtons={false}
                                isDisabled
                            />
                            {/* <InputField
                                label="Select Duration"
                                placeholder="Enter duration"
                                type="number"
                                value={formData.SelectDurationValue || getDisplayDuration(formData.durations, formData.SelectDurationUnit || 'Years')}
                                onChange={(e: any) => {
                                    handleDurationChange(e.target.value, formData.SelectDurationUnit || 'Years');
                                }}
                                showTimeUnitDropdown={true}
                                timeUnitValue={formData.SelectDurationUnit || 'Years'}
                                onTimeUnitChange={(val) => {
                                    const displayValue = getDisplayDuration(formData.durations, val);
                                    handleDurationChange(displayValue.toString(), val);
                                }}
                            /> */}

                            <InputField
                                label="Select Duration"
                                placeholder="Enter New Duration"
                                type="number"
                                value={formData.SelectDurationValue}
                                onChange={(e: any) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    handleDurationChange(value, formData.SelectDurationUnit || 'Years');
                                }}
                                showTimeUnitDropdown={true}
                                timeUnitValue={formData.SelectDurationUnit || 'Years'}
                                onTimeUnitChange={(val) => {
                                    const displayValue = getDisplayDuration(formData.durations, val);
                                    handleDurationChange(displayValue.toString(), val);
                                }}
                            />

                            <DateField
                                label="Start Date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={(date) =>
                                    setFormData({ ...formData, startDate: date })
                                }
                                minDate={dayjs()}
                            />
                            <InputField
                                label='Selected Devices'
                                placeholder='Select Devices'
                                value={
                                    formData.deviceIds
                                        ? formData.deviceIds.slice(0, 3).join(', ') +
                                        (formData.deviceIds.length > 3 ? ` +${formData.deviceIds.length - 3} more` : '')
                                        : ''
                                }
                                onChange={(e: any) => setFormData({ ...formData, selectedDevice: e })}
                                isDisabled
                            />
                            <InputField
                                label="Selected Price"
                                placeholder="Add Price"
                                type="number"
                                value={formData.totalPrice}
                                onChange={handleChange('totalPrice')}
                                isDisabled
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="flex justify-between w-full gap-4 mb-4">
                <CustomIconButton
                    text="Back"
                    onClick={handleBack}
                    variant="outlined"
                    background="transparent"
                />

                {activeStep === 1 && !isSummaryView && (
                    <Button
                        text="View Summary"
                        onClick={handleViewSummary}
                        disabled={!formData || !formData.productItem || !formData.SelectDurationValue ||
                            !formData.SelectDurationUnit}
                        fullWidth={false}
                    />
                )}
            </div>

        </div>
    );
};

export default RenewOrderDetailsForm;
